/**
 * Collapsible panel section — clear, move up/down, max 10 visible with internal scroll.
 *
 * COPIED VERBATIM from pages/dev-agent/components/rightPanels/PanelSection.tsx.
 * Pure presentational (no store dependency); kept byte-for-byte so the
 * multi-agent panels match dev-agent colours / look exactly.
 */
import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Trash2 } from "lucide-react";

export function PanelSection({
  title,
  icon,
  count,
  children,
  onClear,
  onMoveUp,
  onMoveDown,
  disabled,
}: {
  title: string;
  icon: React.ReactNode;
  count: number;
  children: React.ReactNode;
  onClear?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  disabled?: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasContent = count > 0;
  return (
    <div
      className={`border-b border-border/30 ${hasContent && expanded ? "bg-primary/5 border-l-2 border-l-primary" : ""} ${disabled ? "opacity-50 pointer-events-none" : ""}`}
    >
      <div className="flex items-center gap-1 w-full px-3 py-1.5 group">
        <button
          type="button"
          onClick={() => !disabled && setExpanded(!expanded)}
          className="flex items-center gap-2 flex-1 hover:bg-muted/30 rounded px-1 py-0.5 transition-colors text-left"
        >
          <span className="text-foreground [&_svg]:shrink-0">{icon}</span>
          <span className="font-semibold text-xs flex-1 text-foreground">{title}</span>
          {expanded ? (
            <ChevronDown className="w-3 h-3 text-foreground" />
          ) : (
            <ChevronRight className="w-3 h-3 text-foreground" />
          )}
        </button>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {onMoveUp && (
            <button
              type="button"
              onClick={onMoveUp}
              title="Move section up"
              aria-label="Move section up"
              className="p-0.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowUp className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          )}
          {onMoveDown && (
            <button
              type="button"
              onClick={onMoveDown}
              title="Move section down"
              aria-label="Move section down"
              className="p-0.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowDown className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          )}
          {onClear && hasContent && (
            <button
              type="button"
              onClick={onClear}
              title="Clear"
              className="p-0.5 rounded hover:bg-ma-err-soft text-muted-foreground hover:text-ma-err-text transition-colors"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-2 max-h-[280px] overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 [&::-webkit-scrollbar-thumb]:rounded-full">
          {children}
        </div>
      )}
    </div>
  );
}
